// Lays out the section board and its side panels without dead space.
// Side panels sit next to the board only while they fit its height; the rest flow into a row below.
// Rail panels are one column wide (a third of the area). Panels below share the row, so heights measured
// in the rail are remembered and used for fitting decisions, which keeps panels from flip-flopping.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import '../concession.css';

const GAP = 24; // matches the CSS gap
const TILE_MIN = 260; // matches .cx-tile-grid minmax
const TILE_GAP = 14; // matches .cx-tile-grid gap
const PANEL_PADDING_X = 48; // .cx-panel horizontal padding
const FIT_SLACK = 60; // allow the rail to run slightly past the board
const SIDE_MIN_WIDTH = 1100; // below this, stack everything

const sameLayout = (a, b) => a.mode === b.mode && a.railKeys.join('|') === b.railKeys.join('|');

const AdaptiveBoardLayout = ({ board, panels, pinFirst = false }) => {
  const containerRef = useRef(null);
  const boardRef = useRef(null);
  const panelEls = useRef({});
  const frame = useRef(null);
  const railHeights = useRef({});
  const [layout, setLayout] = useState({ mode: 'side', railKeys: panels.map((p) => p.key) });
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  const compute = useCallback(() => {
    const container = containerRef.current;
    const boardEl = boardRef.current;
    if (!container || !boardEl) return;

    const width = container.clientWidth;
    const keys = panels.map((p) => p.key);

    // Measure natural heights: panels beside the board are stretched to match it, so switch the stretch off
    // for this read (same frame, never painted) and back on afterwards
    container.classList.add('cx-adaptive-measuring');
    if (width < SIDE_MIN_WIDTH) {
      container.classList.remove('cx-adaptive-measuring');
      const next = { mode: 'stack', railKeys: [] };
      setLayout((prev) => (sameLayout(prev, next) ? prev : next));
      return;
    }

    // Estimate the board's height when it sits beside the rail (two of three columns)
    const columnWidth = (width - GAP * 2) / 3;
    const sideBoardWidth = columnWidth * 2 + GAP;
    const grid = boardEl.querySelector('.cx-tile-grid');
    const tile = grid?.querySelector('.cx-tile');
    let boardHeight = boardEl.offsetHeight;
    if (grid && tile) {
      const count = grid.children.length;
      const header = boardEl.offsetHeight - grid.offsetHeight;
      const perRow = Math.max(1, Math.floor((sideBoardWidth - PANEL_PADDING_X + TILE_GAP) / (TILE_MIN + TILE_GAP)));
      const rows = Math.ceil(count / perRow);
      boardHeight = header + rows * tile.offsetHeight + (rows - 1) * TILE_GAP;
    }

    // Remember heights of panels currently in the rail (their narrowest width)
    if (layoutRef.current.mode === 'side') {
      layoutRef.current.railKeys.forEach((key) => {
        const el = panelEls.current[key];
        if (el) railHeights.current[key] = el.offsetHeight;
      });
    }
    const heights = keys.map((key) =>
      Math.max(panelEls.current[key]?.offsetHeight || 0, railHeights.current[key] || 0)
    );
    container.classList.remove('cx-adaptive-measuring');

    // If even the first panel is much taller than the board, give the board the full width
    // (unless the first panel is pinned beside the board)
    if (!heights.length || (!pinFirst && heights[0] > boardHeight * 1.2)) {
      const next = { mode: 'full', railKeys: [] };
      setLayout((prev) => (sameLayout(prev, next) ? prev : next));
      return;
    }

    const railKeys = [];
    let used = 0;
    for (let i = 0; i < keys.length; i++) {
      const add = heights[i] + (railKeys.length ? GAP : 0);
      const pinned = pinFirst && i === 0;
      if (!pinned && used + add > Math.max(boardHeight, pinFirst ? heights[0] : 0) + FIT_SLACK) break;
      railKeys.push(keys[i]);
      used += add;
    }
    const next = railKeys.length ? { mode: 'side', railKeys } : { mode: 'full', railKeys: [] };
    setLayout((prev) => (sameLayout(prev, next) ? prev : next));
  }, [panels, pinFirst]);

  const schedule = useCallback(() => {
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(compute);
  }, [compute]);

  // Recompute whenever anything resizes (window, board content, panel content)
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(schedule);
    if (containerRef.current) observer.observe(containerRef.current);
    if (boardRef.current) observer.observe(boardRef.current);
    Object.values(panelEls.current).forEach((el) => el && observer.observe(el));
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame.current);
    };
  });

  useLayoutEffect(() => {
    compute();
  }, [compute]);

  const setPanelRef = (key) => (el) => {
    if (el) panelEls.current[key] = el;
    else delete panelEls.current[key];
  };

  const rail = layout.mode === 'side' ? panels.filter((p) => layout.railKeys.includes(p.key)) : [];
  const rest = panels.filter((p) => !rail.includes(p));
  const renderPanel = (p) => (
    <div key={p.key} ref={setPanelRef(p.key)} className="cx-adaptive-item">
      {p.node}
    </div>
  );

  return (
    <div ref={containerRef} className={`cx-adaptive cx-adaptive-${layout.mode}`}>
      <div className="cx-adaptive-top">
        <div ref={boardRef} className="cx-adaptive-board">{board}</div>
        {rail.length > 0 && <div className="cx-adaptive-rail">{rail.map(renderPanel)}</div>}
      </div>
      {rest.length > 0 && (
        <div
          className={`cx-adaptive-rest${layout.mode !== 'stack' && rest.length <= 2 ? ' cx-adaptive-rest-wide' : ''}`}
          style={layout.mode === 'stack' ? undefined : { gridTemplateColumns: `repeat(${Math.min(3, rest.length)}, minmax(0, 1fr))` }}
        >
          {rest.map(renderPanel)}
        </div>
      )}
    </div>
  );
};

export default AdaptiveBoardLayout;
